import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { Card, Typography, Tag, Button } from 'antd';
import { RightOutlined, ReloadOutlined } from '@ant-design/icons';

import { Banner } from '../../types/banner';
import { MenuSections } from '../../types/menu-sections';
import { tokens } from '../../theme/tokens';
import { useModal } from '../../context/modal';

import { getDisplayContent, getBannerStatusData, addCacheBusting, getFirstImage } from '../../utils';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';

dayjs.extend(relativeTime);
dayjs.locale('es');

const { Text } = Typography;

interface RecentBannersListProps {
  items: Banner[];
  onRefresh: () => void;
  loading?: boolean;
  onNavigate?: (section: MenuSections) => void;
}

export const RecentBannersList: React.FC<RecentBannersListProps> = ({
  items,
  onRefresh,
  loading = false,
  onNavigate
}) => {
  const { openAsset } = useModal();
  const { fontSize, spacing, screens } = useResponsiveSizes();

  return (
    <div style={{ flex: 1 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: spacing.sm }}>
          <Typography.Title style={{ fontSize: fontSize.xl }}>Últimos assets</Typography.Title>
          <Button
            type='text'
            icon={<ReloadOutlined />}
            onClick={() => onRefresh()}
            loading={loading}
            style={{ color: '#24242470', position: 'relative', bottom: '2px' }}
          />
        </div>
        <span
          style={{
            fontSize: fontSize.lg,
            color: tokens.colors.primary.neutral[1000],
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
            fontWeight: 500
          }}
          onClick={() => onNavigate?.('library')}
        >
          Ver todos <RightOutlined style={{ fontSize: fontSize.sm }} />
        </span>
      </div>

      {/* Lista de banners */}
      <Card styles={{ body: { padding: `${spacing.sm}px ${spacing.sm}px` } }}>
        {items.map(item => {
          const imageUrl = getFirstImage(item);
          const imageUrlWithCache = imageUrl ? addCacheBusting(imageUrl, item.updatedAt) : undefined;
          const showImage = item.status === 'DONE' && imageUrl;

          return (
            <div
              key={item.bannerId}
              style={{
                padding: `${spacing.sm}px ${spacing.sm}px`,
                cursor: 'pointer',
                borderRadius: '6px',
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: spacing.md,
                minHeight: '60px'
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f7fb')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={() => openAsset(item.bannerId)}
            >
              {/* Contenido principal: imagen + info */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.md,
                  flex: 1,
                  minWidth: 0 // Permite que el texto se trunque correctamente
                }}
              >
                {/* Preview thumbnail */}
                {showImage && (
                  <img
                    src={imageUrlWithCache}
                    alt='Banner preview'
                    style={{
                      width: screens.xs ? '50px' : '75px',
                      height: screens.xs ? '28px' : '45px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      flexShrink: 0
                    }}
                  />
                )}

                {/* Info del banner */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0, // Permite que el contenido se trunque
                    alignItems: 'flex-start'
                  }}
                >
                  <Text
                    strong
                    style={{
                      fontSize: fontSize.base,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {getDisplayContent(item)}
                  </Text>
                  <Text style={{ fontSize: fontSize.sm, color: '#888' }}>{dayjs(item.createdAt).fromNow()}</Text>
                </div>
              </div>

              {/* Tag de estado - siempre visible */}
              <Tag
                style={{
                  height: '24px',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  backgroundColor: tokens.colors.primary.white,
                  fontSize: fontSize.xs,
                  padding: `0 ${spacing.sm}px`,
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  flexShrink: 0,
                  border: '1px solid #f0f0f0'
                }}
              >
                {getBannerStatusData(item)?.label || 'Enviado'}
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: getBannerStatusData(item)?.background || 'gray',
                    flexShrink: 0
                  }}
                />
              </Tag>
            </div>
          );
        })}
      </Card>
    </div>
  );
};

export default RecentBannersList;
